import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import sys from '@truckdown/jsclient';
import { framework } from '@truckdown/jsclient/apis';

interface IShowOptions {
    phone: boolean,
    fax: boolean,
    afterHours: boolean,
    tollFree: boolean
}

interface IPhoneNumber {
    id: string,
    l: string,
    n: string,
    d: string
}

interface IPhoneNumbers {
    phone?: string,
    fax?: string,
    afterHours?: string,
    tollFree?: string
}

var getNumbers = function (locationId: string): Promise<Response> {
    var service = sys.framework.getRequiredService<framework.IWebServiceManager>('IWebServiceManager');

    let req = service.createRequest({
        service: service.main,
        path: '/listing/add-call/' + encodeURIComponent(locationId),
        method: framework.HttpMethod.GET
    });

    return service.send(req);
}

var isDefined = function (variable: any): boolean {
    if (typeof variable !== 'undefined') {
        if (variable != null) {
            return true;
        }
    }
    return false;
};

var isFunction = function (variable: any): boolean {
    if (isDefined(variable)) {
        return (variable.constructor && variable.call && variable.apply);
    }
    return false;
};

var logEvent = function (cat: string, action: string, label: string): void {
    if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
            'event': action,
            'event_category': cat,
            'event_label': label
        });
        console.log('event: ' + action, cat, label);
    }
}
export default defineComponent({
    name: 'call-control',
    props: {
        locationId: String,
        showPhone: Boolean,
        showFax: Boolean,
        showAfterHours: Boolean,
        showTollFree: Boolean,
        canEdit: Boolean,
        phonenum: String,
        faxnum: String,
        afterhoursnum: String,
        tollfreenum: String
    },
    setup: function (props, context) {
        //data
        const displayNumbers = ref<boolean>(false);
        const show = ref<IShowOptions>({
            phone: props.showPhone,
            fax: props.showFax,
            afterHours: props.showAfterHours,
            tollFree: props.showTollFree
        });
        const showNumbers = ref<boolean>(false);
        const phoneNumbers = ref<IPhoneNumbers>({
            phone: props.phonenum,
            fax: props.faxnum,
            afterHours: props.afterhoursnum,
            tollFree: props.tollfreenum
        });
        const showCaptcha = ref<boolean>(false);
        const isLoadingNumbers = ref<boolean>(false);
        const showWarning = ref<boolean>(false);
        const isBlocked = ref<boolean>(false);

        //computed

        const hasNumbers = computed((): boolean => {
            return (
                hasPhone.value || hasFax.value || hasAfterHours.value || hasTollFree.value
            );
        });
        const hasPhone = computed((): boolean => {
            if (phoneNumbers.value.phone) {
                return phoneNumbers.value.phone.length > 0;
            }
            return false;
        });
        const hasFax = computed((): boolean => {
            if (phoneNumbers.value.fax) {
                return phoneNumbers.value.fax.length > 0;
            }
            return false;
        });
        const hasAfterHours = computed((): boolean => {
            if (phoneNumbers.value.afterHours) {
                return phoneNumbers.value.afterHours.length > 0;
            }
            return false;
        });
        const hasTollFree = computed((): boolean => {
            if (phoneNumbers.value.tollFree) {
                return phoneNumbers.value.tollFree.length > 0;
            }
            return false;
        });

        const shouldShowPhone = computed((): boolean => {
            if (showNumbers.value) {
                return hasPhone.value;
            }
            return false;
        });
        const shouldShowFax = computed((): boolean => {
            if (showNumbers.value) {
                return hasFax.value;
            }
            return false;
        });
        const shouldShowAfterHours = computed((): boolean => {
            if (showNumbers.value) {
                return hasAfterHours.value;
            }
            return false;
        });
        const shouldShowTollFree = computed((): boolean => {
            if (showNumbers.value) {
                return hasTollFree.value;
            }
            return false;
        });

        //methods

        const setPhoneNumbers = function (phones: IPhoneNumber[]): void {
            for (let i = 0; i < phones.length; i++) {
                switch (phones[i].l) {
                    case "Phone":
                        phoneNumbers.value.phone = phones[i].d;
                    case "Fax":
                        phoneNumbers.value.fax = phones[i].d;
                    case "After Hours":
                        phoneNumbers.value.afterHours = phones[i].d;
                    case "Toll Free":
                        phoneNumbers.value.tollFree = phones[i].d;
                    default:
                        break;
                }
            }
            showNumbers.value = true;
        };
        const displayPhoneNumbers = async function (ev?: Event): Promise<void> {
            if (!isLoadingNumbers.value) {
                isLoadingNumbers.value = true;

                try {
                    if (hasNumbers.value) {
                        showNumbers.value = true;
                        sys.events.emit('captcha-success', props.locationId);
                    } else {
                        var numbers = await getNumbers(props.locationId ?? '');
                        logEvent('Listing', 'Call', 'Call');

                        showCaptcha.value = false;
                        showWarning.value = false;
                        if (numbers.ok) {
                            var warnHeader = numbers.headers.get('X-Throttle-Warn');
                            if (warnHeader != null) {
                                //warning.value = warnHeader;
                                showWarning.value = true;
                            }

                            var res = await numbers.json() as IPhoneNumber[];
                            setPhoneNumbers(res);
                            sys.events.emit('captcha-success', props.locationId);
                        } else {
                            if (numbers.status == 429) {
                                showCaptcha.value = true;
                            } else if (numbers.status == 403) {
                                isBlocked.value = true;
                            } else {
                                console.log(numbers);
                            }
                        }
                    }

                } finally {
                    isLoadingNumbers.value = false;
                }
            }
        };
        const displayCaptcha = function () {
            showCaptcha.value = true;
        };

        onMounted(() => {
            sys.events.on('captcha-success', async function (instance: string) {
                showNumbers.value = false;
                if (instance === props.locationId) {
                    if (showCaptcha.value) {
                        await displayPhoneNumbers();
                    } else {
                        showNumbers.value = true;
                    }
                    showCaptcha.value = false;
                }
            });
        });

        return {
            displayNumbers,
            show,
            showNumbers,
            phoneNumbers,
            showCaptcha,
            isLoadingNumbers,
            showWarning,
            isBlocked,

            hasNumbers,
            hasPhone,
            hasFax,
            hasAfterHours,
            hasTollFree,
            shouldShowPhone,
            shouldShowFax,
            shouldShowAfterHours,
            shouldShowTollFree,

            setPhoneNumbers,
            displayPhoneNumbers,
            displayCaptcha
        };
    }
});
